import {
  CallFeatureEnum,
  useDialerCallPulseSubscription,
  useGetCallSettingForChannelQuery,
} from '@/types/generated/graphql';
import { useDeviceStore } from '@modules/phone/store/device.store';
import { getProvider } from '@/lib/callProviders/provider-registry';
import { useDialerCallPulseStore } from '@modules/phone/store/dialer-call-pulse.store';
import { useDialerStore } from '@modules/phone/store/dialer.store';
import { CallAnsweredByData, CallStatusEnum } from '@modules/phone/types/store/dialer-call-pulse.types';
import { notifyCallAnsweredElsewhere } from '@shared/app-bootstrap/subscription-manager/use-conversation-subscriptions/call-answered-elsewhere-handler';
import { useRef } from 'react';

export const useDialerCallPulse = () => {
  const callRef = useRef<string | null>(null);
  const { call } = useDeviceStore();
  const { channelId } = useDialerStore();
  const {
    setIsSubscriptionConnected,
    appendRecordingTimestamp,
    updateCallStatus,
    resetCallStatus,
    setIsRecording,
    setAutoRecording,
    setCallTransferEligible,
  } = useDialerCallPulseStore();

  const callSid = call ? getProvider(call.provider)?.getCallId(call) : undefined;
  if (callSid) {
    callRef.current = callSid;
  }

  // check if auto recording and call transfer are enabled in the calling number
  useGetCallSettingForChannelQuery({
    fetchPolicy: 'no-cache',
    variables: {
      channelId: channelId as number,
    },
    onCompleted: (data) => {
      const { callTransfer, recording } = data?.getCallSettingForChannel?.data?.callSetting || {};
      setAutoRecording(!!recording);
      setCallTransferEligible(!!callTransfer);
    },
    skip: !channelId,
  });

  // dialer call pulse subscription
  useDialerCallPulseSubscription({
    variables: {
      identity: (callRef?.current ?? undefined) as string,
    },

    onData: (data) => {
      const response = data?.data?.data?.dialerCallPulse;
      const feature = response?.feature;

      if (feature === CallFeatureEnum.call_answered_by) {
        const answeredBy = response?.data as CallAnsweredByData | undefined;
        notifyCallAnsweredElsewhere({
          clientId: answeredBy?.client?.id,
          receiverName: answeredBy?.agent?.user?.name,
          receiverAvatarUrl: answeredBy?.agent?.user?.profilePicture?.url,
          callerName: answeredBy?.client?.name,
          messageConversationId: answeredBy?.id,
          agentId: answeredBy?.agent?.user?.id,
        });
      } else if (feature === CallFeatureEnum.carrier_leg_connected) {
        setIsSubscriptionConnected(true);
      } else if (feature === CallFeatureEnum.recording_started) {
        setIsSubscriptionConnected(true);
        appendRecordingTimestamp({
          timestamp: performance.now(),
          type: 'resume',
          isSuccess: true,
        });
        setIsRecording(true);
      } else if (feature === CallFeatureEnum.transfer_completed) {
        const status = response?.data?.status;

        // only handled in-progress status while feature is transfer_completed
        // other than that all status are used as transfer-failed in frontend side
        // when call transfer is failed reset the callStatus after 8 seconds
        if (status === 'in-progress') {
          updateCallStatus(CallStatusEnum.transfer_completed);
        } else {
          updateCallStatus(CallStatusEnum.transfer_failed);
          setTimeout(resetCallStatus, 8_000);
        }
      }
    },
    skip: !callRef?.current,
  });
};
